import { Suspense } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { useAuth } from '@/lib/auth/AuthContext';
import type { ProfilePageQuery } from '@/__generated__/ProfilePageQuery.graphql';

/**
 * Query to fetch profile data: viewer info and social stats
 */
const profilePageQuery = graphql`
  query ProfilePageQuery {
    viewer {
      id
      userId
      email
      role
      isAdmin
      lastLoginAt
    }
    followedTopics(first: 1) {
      totalCount
    }
    likedEpisodes(first: 1) {
      totalCount
    }
  }
`;

interface StatCardProps {
  label: string;
  value: number;
  icon: React.ReactNode;
}

function StatCard({ label, value, icon }: StatCardProps) {
  return (
    <div className="rounded-xl border border-gray-800 bg-gradient-to-b from-gray-800/50 to-gray-900/50 p-6">
      <div className="flex items-center gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-600/20 text-indigo-400">
          {icon}
        </div>
        <div>
          <p className="text-2xl font-bold text-white">{value}</p>
          <p className="text-sm text-gray-400">{label}</p>
        </div>
      </div>
    </div>
  );
}

function ProfilePageContent() {
  const { logout } = useAuth();
  const data = useLazyLoadQuery<ProfilePageQuery>(
    profilePageQuery,
    {},
    { fetchPolicy: 'store-and-network' }
  );

  const viewer = data.viewer;
  const followedCount = data.followedTopics.totalCount ?? 0;
  const likedCount = data.likedEpisodes.totalCount ?? 0;

  if (!viewer) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-400">Unable to load profile. Please try again.</p>
      </div>
    );
  }

  const lastLoginDate = viewer.lastLoginAt
    ? new Date(viewer.lastLoginAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      {/* Profile Header */}
      <div className="text-center mb-8">
        {/* Avatar placeholder */}
        <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-indigo-600 to-purple-600">
          <span className="text-3xl font-bold text-white">
            {viewer.email.charAt(0).toUpperCase()}
          </span>
        </div>
        <h1 className="text-2xl font-bold text-white">{viewer.email}</h1>
        <div className="mt-2 flex items-center justify-center gap-2">
          {viewer.isAdmin && (
            <span className="inline-flex items-center rounded-full bg-indigo-600/20 px-2.5 py-0.5 text-xs font-medium text-indigo-400">
              Admin
            </span>
          )}
          <span className="inline-flex items-center rounded-full bg-gray-700/50 px-2.5 py-0.5 text-xs font-medium text-gray-400 capitalize">
            {viewer.role}
          </span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 mb-8">
        <StatCard
          label="Topics Followed"
          value={followedCount}
          icon={
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M18 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM4 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 10.374 21c-2.331 0-4.512-.645-6.374-1.766Z"
              />
            </svg>
          }
        />
        <StatCard
          label="Episodes Liked"
          value={likedCount}
          icon={
            <svg
              className="h-6 w-6"
              fill="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z" />
            </svg>
          }
        />
      </div>

      {/* Account Info */}
      <div className="rounded-xl border border-gray-800 bg-gradient-to-b from-gray-800/50 to-gray-900/50 p-6 mb-8">
        <h2 className="text-lg font-semibold text-white mb-4">Account Information</h2>
        <dl className="space-y-4">
          <div className="flex justify-between">
            <dt className="text-gray-400">Email</dt>
            <dd className="text-white">{viewer.email}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-400">User ID</dt>
            <dd className="text-white font-mono text-sm truncate max-w-[200px]" title={viewer.userId}>
              {viewer.userId}
            </dd>
          </div>
          {lastLoginDate && (
            <div className="flex justify-between">
              <dt className="text-gray-400">Last Login</dt>
              <dd className="text-white">{lastLoginDate}</dd>
            </div>
          )}
        </dl>
      </div>

      {/* Sign Out Button */}
      <div className="text-center">
        <button
          type="button"
          onClick={logout}
          className="inline-flex items-center gap-2 rounded-lg border border-red-600/50 bg-red-600/10 px-6 py-3 text-sm font-medium text-red-400 transition-colors hover:bg-red-600/20 hover:border-red-500/50"
        >
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9"
            />
          </svg>
          Sign Out
        </button>
      </div>
    </div>
  );
}

export function ProfilePage() {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
        </div>
      }
    >
      <ProfilePageContent />
    </Suspense>
  );
}
